import { F0Box, F0Dialog, F0Form, F0Text, f0FormField, useF0Form } from '@factorialco/f0-react'
import type { CustomFieldRenderProps } from '@factorialco/f0-react'
import { Input, NumberInput, Switch } from '@factorialco/f0-react/dist/experimental'
import { EditTrainingBudgetFragment } from 'generated/resources/graphql'
import { useMemo, useRef, useState } from 'react'
import { z } from 'zod'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { fromCents, toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  useArchiveBudget,
  useBudgetsPage,
  useUnarchiveBudget,
  useUpdateBudget,
} from 'modules/finance/hooks/useBudgets'

interface Props {
  budget: EditTrainingBudgetFragment
  onClose: () => void
  onSuccess: () => void
}

export const EditTrainingBudgetDialog = ({ budget, onClose, onSuccess }: Props) => {
  const [archived, setArchived] = useState(budget.archived)
  const archivedRef = useRef(archived)
  archivedRef.current = archived

  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { legalEntities } = useBudgetsPage(companyId)

  const { updateBudget } = useUpdateBudget()
  const { archiveBudget } = useArchiveBudget()
  const { unarchiveBudget } = useUnarchiveBudget()

  const legalEntity = legalEntities?.find((node) => node.id == budget.legalEntityId)

  const { formRef, submit, isSubmitting, hasErrors } = useF0Form()

  const isArchived = archived

  const schema = useMemo(
    () =>
      z
        .object({
          name: f0FormField(z.string().min(1).max(280), {
            label: i18n.t('budgets.forms.edit_training_budget.title'),
            placeholder: i18n.t('budgets.forms.edit_training_budget.title_placeholder'),
            disabled: isArchived,
          }),
          description: f0FormField(z.string().max(1000).optional(), {
            label: i18n.t('budgets.forms.edit_training_budget.description'),
            placeholder: i18n.t('budgets.forms.edit_training_budget.description_placeholder'),
            fieldType: 'textarea',
            disabled: isArchived,
          }),
          effectiveFrom: f0FormField(z.date(), {
            label: i18n.t('budgets.forms.edit_training_budget.start_date'),
            placeholder: i18n.t('budgets.forms.edit_training_budget.start_date_placeholder'),
            row: 'dates',
            disabled: isArchived,
          }),
          effectiveUntil: f0FormField(z.date().optional(), {
            label: i18n.t('budgets.forms.edit_training_budget.end_date'),
            placeholder: i18n.t('budgets.forms.edit_training_budget.end_date_placeholder'),
            row: 'dates',
            disabled: isArchived,
            minDate: ({ values }: { values: Record<string, unknown> }) =>
              values.effectiveFrom ? new Date(`${values.effectiveFrom}`) : undefined,
          }),
          amount: f0FormField(z.number().positive(), {
            label: i18n.t('budgets.forms.edit_training_budget.amount'),
            fieldType: 'custom',
            disabled: isArchived,
            render: ({ value, onChange, disabled }: CustomFieldRenderProps<number>) => (
              <NumberInput
                required
                label={i18n.t('budgets.forms.edit_training_budget.amount')}
                locale={i18n.locale}
                units={budget.currency}
                value={value ?? null}
                onChange={(v: number | null) => onChange(v ?? 0)}
                disabled={disabled}
                hint={i18n.t('budgets.forms.edit_budget.amount_helper_text')}
                maxDecimals={2}
                size="md"
              />
            ),
          }),
        })
        .superRefine((data, ctx) => {
          if (
            data.effectiveFrom &&
            data.effectiveUntil &&
            data.effectiveFrom > data.effectiveUntil
          ) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: i18n.t('budgets.forms.errors.start_date'),
              path: ['effectiveFrom'],
            })
          }
        }),
    [budget.currency, isArchived]
  )

  const defaultValues = useMemo(
    () => ({
      name: budget.name,
      description: budget.description ?? '',
      effectiveFrom: new Date(String(budget.effectiveFrom)),
      effectiveUntil: budget.effectiveUntil ? new Date(String(budget.effectiveUntil)) : undefined,
      amount: fromCents(Number(budget.amountCents)),
    }),
    [budget]
  )

  const handleSubmit = async (data: z.infer<typeof schema>) => {
    const result = await updateBudget({
      id: budget.id,
      legalEntityId: budget.legalEntityId,
      name: data.name,
      description: data.description,
      amountCents: toCents(data.amount),
      effectiveFrom: data.effectiveFrom.toISOString(),
      effectiveUntil: data.effectiveUntil?.toISOString() ?? null,
    })

    let formErrors: Record<string, string> = {}
    const handled = handleResult(result, (errors) => {
      formErrors = errors
    })

    if (!handled.success) {
      return { success: false as const, errors: formErrors }
    }

    const archiveChanged = archivedRef.current !== budget.archived

    if (archiveChanged) {
      if (archivedRef.current) {
        const archiveResult = await archiveBudget({ id: budget.id })
        const archiveHandled = handleResult(archiveResult, () => undefined)

        if (!archiveHandled.success) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('budgets.forms.edit_budget.flash.budget_archive_error'),
          })
          return { success: false as const }
        }
      } else {
        const unarchiveResult = await unarchiveBudget({ id: budget.id })
        const unarchiveHandled = handleResult(unarchiveResult, () => undefined)

        if (!unarchiveHandled.success) {
          app.setFlash({
            type: 'wrong',
            message: i18n.t('budgets.forms.edit_budget.flash.budget_unarchive_error'),
          })
          return { success: false as const }
        }
      }
    }

    app.setFlash({
      type: 'correct',
      message: i18n.t('budgets.forms.edit_budget.flash.budget_updated_ok'),
    })
    onSuccess()
    return { success: true as const }
  }

  return (
    <F0Dialog
      isOpen
      position="right"
      title={i18n.t('budgets.forms.edit_budget.title')}
      onClose={onClose}
      primaryAction={{
        label: i18n.t('common.save'),
        onClick: submit,
        disabled: isSubmitting || hasErrors,
        loading: isSubmitting,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <F0Box display="flex" flexDirection="column" gap="lg">
        <Input
          disabled
          label={i18n.t('budgets.forms.edit_training_budget.legal_entity')}
          value={legalEntity?.legalName ?? ''}
          onChange={() => undefined}
          size="md"
        />

        <F0Form
          formRef={formRef}
          name="edit-training-budget"
          schema={schema}
          defaultValues={defaultValues}
          submitConfig={{ type: 'default', hideSubmitButton: true }}
          onSubmit={handleSubmit}
        />

        <F0Box
          display="flex"
          flexDirection="row"
          alignItems="center"
          justifyContent="between"
          padding="lg"
          borderRadius="md"
          gap="md"
          border="default"
          borderColor="secondary"
        >
          <F0Box display="flex" flexDirection="column" gap="md">
            <F0Text variant="label" content={i18n.t('budgets.forms.edit_budget.archive.label')} />
            <F0Text content={i18n.t('budgets.forms.edit_training_budget.archive_description')} />
          </F0Box>
          <Switch
            id="archive-budget-toggle"
            checked={archived}
            onCheckedChange={(checked) => setArchived(checked)}
          />
        </F0Box>
      </F0Box>
    </F0Dialog>
  )
}
